import React from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { ColorStyle, ViewMode } from '../api'

type Props = {
    viewMode: ViewMode
    onChangeViewMode: (viewMode: ViewMode) => void
    boxSize: string
    onChangeBoxSize: (boxSize: string) => void
    colorStyle: ColorStyle
    onChangeColorStyle: (colorStyle: ColorStyle) => void
    colorTextEnabled: boolean
    onChangeColorTextEnabled: (colorTextEnabled: boolean) => void
}

export const DisplayTab = React.memo(function DisplayTab({
    viewMode,
    onChangeViewMode,
    boxSize,
    onChangeBoxSize,
    colorStyle,
    onChangeColorStyle,
    colorTextEnabled,
    onChangeColorTextEnabled
}: Props): JSX.Element {
    return (
        <TabBodyPadding>
            <Section>
                <SectionRow>
                    <SectionLabel>{i18n('View mode')}</SectionLabel>
                    <Segmented>
                        <SegButton
                            className={viewMode === 'cards' ? 'active' : ''}
                            onClick={() => onChangeViewMode('cards')}
                        >
                            <i className="fa fa-th-large" />
                            {i18n('Cards')}
                        </SegButton>
                        <SegButton
                            className={viewMode === 'table' ? 'active' : ''}
                            onClick={() => onChangeViewMode('table')}
                        >
                            <i className="fa fa-table" />
                            {i18n('Table')}
                        </SegButton>
                    </Segmented>
                </SectionRow>
            </Section>
            <Section>
                <SectionRow>
                    <SectionLabel>{i18n('Density')}</SectionLabel>
                    <Segmented>
                        <SegButton
                            className={boxSize === 'small' ? 'active' : ''}
                            onClick={() => onChangeBoxSize('small')}
                        >
                            {i18n('Compact')}
                        </SegButton>
                        <SegButton
                            className={boxSize === 'medium' ? 'active' : ''}
                            onClick={() => onChangeBoxSize('medium')}
                        >
                            {i18n('Comfortable')}
                        </SegButton>
                        <SegButton
                            className={boxSize === 'large' ? 'active' : ''}
                            onClick={() => onChangeBoxSize('large')}
                        >
                            {i18n('Spacious')}
                        </SegButton>
                    </Segmented>
                </SectionRow>
                <SectionHint>{i18n('Applies to both Cards and Table view.')}</SectionHint>
            </Section>
            <Section>
                <SectionRow>
                    <SectionLabel>{i18n('Color style')}</SectionLabel>
                    <Segmented>
                        <SegButton
                            className={colorStyle === 'circle' ? 'active' : ''}
                            onClick={() => onChangeColorStyle('circle')}
                        >
                            <PreviewCircle />
                            {i18n('Circle')}
                        </SegButton>
                        <SegButton
                            className={colorStyle === 'bar' ? 'active' : ''}
                            onClick={() => onChangeColorStyle('bar')}
                        >
                            <PreviewBar />
                            {i18n('Bar')}
                        </SegButton>
                    </Segmented>
                </SectionRow>
                <SectionRow>
                    <SectionLabel />
                    <CheckboxLabel>
                        <input
                            type="checkbox"
                            checked={colorTextEnabled}
                            onChange={event => onChangeColorTextEnabled(event.target.checked)}
                        />
                        {i18n('Also tint channel name')}
                    </CheckboxLabel>
                </SectionRow>
                <SectionHint>
                    {i18n(
                        'Circle and Bar are alternatives for the channel color indicator. Tinting the channel name can be combined with either one, or left off to keep names in their standard color.'
                    )}
                </SectionHint>
            </Section>
        </TabBodyPadding>
    )
})

const TabBodyPadding = styled.div`
    height: 100%;
    width: 100%;
    padding: 24px;
    background-color: var(--gray-200);
`

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    & + & {
        margin-top: 24px;
    }
`

const SectionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`

// fixed width, right-aligned so "View mode" and "Density" share one right edge and the segmented
// buttons that follow both start at the same x, regardless of which label is longer
const SectionLabel = styled.span`
    flex-shrink: 0;
    width: 90px;
    text-align: right;
    font-size: 14px;
    line-height: 21px;
    font-weight: 400;
    color: var(--gray-800);
`

// indented to start at the same x as the first segmented button ("Compact") - label width (90px) + row gap (16px)
const SectionHint = styled.p`
    margin: 0 0 0 106px;
    font-size: 11px;
    line-height: 16px;
    color: var(--gray-600);
`

const Segmented = styled.div`
    display: flex;
    gap: 8px;
`

const SegButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    padding: 8px 16px;
    border: 1px solid var(--gray-400);
    border-radius: 4px;
    background-color: var(--gray-0);
    color: var(--gray-1000);
    cursor: pointer;

    > i {
        font-size: 13px;
    }

    &.active {
        border-color: var(--blue-500);
        color: var(--blue-500);
        background-color: var(--gray-0);
    }

    &:not(.active):hover {
        background-color: var(--gray-200);
    }
`

// background-color: currentColor picks up SegButton's own text color, so these automatically turn
// blue when their button is active, with no extra state to track
const PreviewCircle = styled.span`
    display: inline-block;
    height: 10px;
    width: 10px;
    border-radius: 50%;
    background-color: currentColor;
`

const PreviewBar = styled.span`
    display: inline-block;
    height: 12px;
    width: 4px;
    border-radius: 2px;
    background-color: currentColor;
`

// sits in a SectionRow next to an empty SectionLabel spacer, so it lines up with the segmented
// buttons above without needing its own margin
const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    color: var(--gray-1000);
    cursor: pointer;

    > input {
        cursor: pointer;
    }
`
